import type { ComponentType } from 'react'
import type { RouteObject } from 'react-router'
import { LoginRoute } from './auth/LoginRoute'
import { SetPasswordRoute } from './auth/SetPasswordRoute'
import { has, useSession } from './auth/session'
import { reportClientError } from './api/report'
import { ErrorBoundary, Fallback, RouteErrorFallback } from './shell/ErrorBoundary'
import { AppShell, NoAreasScreen, NotFoundScreen } from './shell/AppShell'
import { NoAccessScreen } from './shell/NoAccessScreen'
import { NAV_ITEMS } from './shell/nav'
import type { NavItem, NavPath } from './shell/nav'

/**
 * The real route table (Task 11). Replaces the Task 8 stub, whose own doc
 * comment named this task as the one that would.
 *
 * `/`, `/login` and `/set-password` are unauthenticated-reachable top-level
 * routes (the last two ARE the sign-in path — nothing above them can gate
 * them). Every capability-gated area nests under `AppShell` as a CHILD
 * route, so `AppShell` (mounted once, at `/`) is the single place that
 * decides "signed out → /login" and "at / → wherever `landingFor` says" —
 * see that file's own doc comment for why redirect ordering matters and how
 * the `isLoading` race was verified.
 *
 * The seven area routes are GENERATED from `shell/nav.ts`'s `NAV_ITEMS`
 * (web-pre-deploy item 6), not written out here one by one — see
 * `AREA_MODULES` and `gateArea` below for the mechanism, and `NoAccessScreen`
 * for what a refused principal actually sees. Enforcement used to be
 * NAV-ONLY: `visibleNav` hid an item whose capability the principal lacked,
 * and nothing whatsoever stopped a bookmark, a typed URL or a stale link
 * reaching the route behind it. Generating both halves from the one table is
 * what makes that gap structurally impossible rather than merely fixed — a
 * new area cannot be added without declaring the capability that gates it,
 * because there is nowhere to add it except the table `visibleNav` already
 * reads. A per-route wrapper (the obvious alternative) would have been a
 * thing the eighth area could forget.
 *
 * `lazy` on each area route (seven — `costs` and `portal` added in
 * review round 1, Important 2, alongside the original five) AND
 * `/styleguide` — route-level code splitting from day one (architecture
 * §8): every one of these chunks is fully precached by Task 14, so this is
 * a parse-cost and cache-granularity decision, never a network fetch a
 * real installed client would wait on. `/no-areas` is deliberately NOT
 * lazy: it's a few lines already co-located in `AppShell.tsx` (see that
 * file's own doc comment for why it isn't a sixth `features/*` folder), and
 * splitting a component that small into its own chunk would trade one HTTP
 * round trip during navigation for no measurable parse-cost saving.
 *
 * `{ path: '*' }`, a TOP-LEVEL sibling of `/login` rather than a child of
 * `AppShell` — review round 1's Important 3. Without it, an unmatched URL
 * (measured: `/does-not-exist`) rendered react-router's own DEVELOPMENT
 * error boundary, a debugging aid never meant for a real visitor. Kept at
 * the top level (not nested under `AppShell`) so it answers regardless of
 * sign-in state, with no dependency on `useSession()` ever resolving.
 * `element`, not `errorElement` — see `NotFoundScreen`'s own doc comment
 * for why those are different problems. DONE, task 12: every OTHER route
 * below now carries the mechanisms that comment used to defer.
 *
 * HOW A THROWN ROUTE OR RENDER ERROR IS CAUGHT — three cases, not two, and
 * the third was found only by running it, not by reading react-router's
 * docs:
 *
 *  1. A THROW DURING THE RENDER of an already-loaded component (a bug in a
 *     component body — the everyday case). Caught by `<ErrorBoundary>`
 *     (`shell/ErrorBoundary.tsx`, a REACT class component), wrapped around
 *     EACH element individually — `AppShell`, and each area's resolved
 *     component — rather than once around the whole tree. React walks up
 *     the actual component tree to find the NEAREST boundary, so an area's
 *     own `<ErrorBoundary>` catches ITS throw before the one wrapping
 *     `AppShell` ever sees it, keeping the nav and header on screen while
 *     only the content pane shows the fallback. CONFIRMED LIVE (task
 *     report): forcing a throw inside a mounted area left "Pipeline" /
 *     "Money" / "Site ops" nav links and "Sign out" all still present and
 *     queryable, with only the content pane replaced.
 *
 *  2. AN UNMATCHED PATH, or a genuine `loader`/`action` throw on a route
 *     that declares no `lazy` of its own (`/login`, `/set-password`, `/`,
 *     `/no-areas` — none of this app's routes have a `loader`/`action`
 *     today, so that half is inert). Caught by `errorElement`
 *     (`RouteErrorFallback`, using `useRouteError()`), which react-router
 *     wraps around the route it is declared on. This IS genuine, WORKING
 *     defense in depth for what a class boundary structurally cannot catch
 *     — an error thrown by `Fallback`'s OWN render, which no boundary can
 *     catch for itself — but only since review round 2's Important 1 fixed
 *     it: `RouteErrorFallback` used to render the SAME shared `Fallback`
 *     component `ErrorBoundary` renders, which measurably made this
 *     scenario WORSE than having no `errorElement` at all (a shared bug
 *     inside `Fallback` threw again with nothing left to catch it, blanking
 *     the page; no `errorElement` at least fell back to react-router's own,
 *     uglier, but non-blank default screen). See `ErrorBoundary.tsx`'s own
 *     doc comment on `RouteErrorFallback` for the measured before/after and
 *     the fix (plain, import-free markup, not a shared component).
 *
 *  3. A `lazy: async () => import(...)` REJECTING — a missing or stale
 *     chunk after a deploy, the one network failure a fully-precached
 *     bundle (a later task) makes newly possible, on EITHER the initial
 *     load or an ordinary client-side navigation into a not-yet-loaded
 *     chunk (measured both ways: starting on a working `/pipeline` and
 *     clicking a `<Link>` into a route whose `lazy` was forced to reject
 *     reproduces the identical empty `<main>`). This is NOT case 2:
 *     `errorElement` declared on the SAME route object as `lazy` was
 *     measured, directly (a synthetic throw inside the loader, a genuinely
 *     unresolvable dynamic import, and a real client-side navigation into
 *     one — all three, in this exact test harness), to never fire for a
 *     lazy rejection — the content pane stays empty forever, no fallback,
 *     no report, the white screen this whole task exists to close.
 *
 *     TRACED, not left as "did not fully trace" (review round 2 did the
 *     tracing this file previously admitted skipping), through
 *     `react-router@8.3.0`'s own source, two hops:
 *       a. `loadLazyRoute` (`react-router/dist/development/lib/router/
 *          router.js:2158`) clears `route.lazy` only on SUCCESS
 *          (`:2185-2187`) and caches the returned promise unconditionally
 *          at `:2190`, BEFORE knowing whether it resolves or rejects — so a
 *          rejection leaves `route.lazy` still truthy, permanently, for the
 *          rest of the page's life (see the `lazyRouteFunctionCache` note
 *          below `lazyArea` for the consequence of that).
 *       b. `_renderMatches` (`react-router/dist/development/lib/hooks.js:
 *          718`) correctly slices `renderedMatches` to INCLUDE the errored
 *          child at `:726-731` (this is where an earlier reading of this
 *          function, before it was actually run, wrongly concluded the
 *          child would stay in the rendered set) — but then re-checks `if
 *          (match.route.lazy || needsToRunLoader)` at `:738-748` for EVERY
 *          match, including the one that just errored, and THAT check is
 *          still true, because step (a)'s rejection never cleared
 *          `route.lazy`. So it truncates AGAIN, this time to
 *          `renderedMatches[0]` — discarding the errored child entirely.
 *          The child's own `errorElement` is no longer in the rendered set
 *          and can never be reached; the parent renders with a null
 *          outlet. That null outlet is this file's measured empty `<main>`.
 *          (With `errorElement` only on the PARENT, `renderedMatches[0]` IS
 *          the errored route, so the truncation lands ON the parent and
 *          its `errorElement` fires instead — replacing the whole layout,
 *          which is what the first measurement in this file's history
 *          showed.)
 *     RE-CHECK THIS on any react-router version bump: the guard at (b) is a
 *      truthiness test on a field the error path at (a) forgets to clear —
 *      exactly the shape of thing a future patch release could fix (or
 *      change differently) without it being a documented breaking change.
 *
 *     `lazyArea` below sidesteps this mechanism entirely rather than
 *     depending on react-router ever fixing it: it `try`/`catch`es the
 *     import itself, reports directly (a plain async function, not a
 *     component render — no purity constraint to respect), and resolves the
 *     lazy loader SUCCESSFULLY with `Fallback` as the "component" if the
 *     import failed. Because this always resolves rather than rejects,
 *     `route.lazy` DOES get cleared (step (a)'s success path runs), and
 *     react-router never re-enters the broken truncation at all. This is
 *     why none of the eight `lazy` routes below declares an `errorElement`.
 *
 * All three report through the SAME function, `reportClientError`
 * (`api/report.ts`), with `kind: 'boundary'` — the brief's own interface
 * names exactly three kinds, and "which of the three caught this" is not
 * information Workers Logs needs to distinguish.
 *
 * EXPORTED (`lazyArea`, not private): `src/routes.test.tsx` calls it
 * directly with a rejecting `load` to cover its catch branch — review round
 * 2's Important 2 found nothing in the committed suite exercised it despite
 * it being the entire justification for this deviation from the brief.
 */
export async function lazyArea(load: () => Promise<ComponentType>): Promise<{ Component: ComponentType }> {
  try {
    const Area = await load()
    return {
      Component: () => (
        <ErrorBoundary>
          <Area />
        </ErrorBoundary>
      ),
    }
  } catch (error) {
    reportClientError(error, { route: window.location.pathname, kind: 'boundary' })
    // `Fallback`'s Reload button is LOAD-BEARING here, not a courtesy: this
    // file's own top comment traces WHY a rejected `lazy` never re-imports
    // on its own — react-router's `loadLazyRoute` caches the rejected
    // promise the FIRST time it runs and never retries, so navigating away
    // from this route and back again re-renders this SAME cached failure,
    // for the rest of the page's life. Only a real reload (a fresh module
    // graph) can recover from a stale chunk after a deploy; that is
    // `Fallback`'s own `window.location.reload()`, and it is the only way
    // out short of the user leaving the page entirely.
    return { Component: Fallback }
  }
}

/**
 * The area chunks, keyed by the `NAV_ITEMS` path that gates each one.
 *
 * AN EXPLICIT RECORD, NOT `import(\`./features/${item.path}\`)` — this is the
 * whole reason the generation below iterates a table and looks a loader up,
 * instead of building the specifier from the path. Vite's dynamic-import
 * analysis (`vite:dynamic-import-vars`) requires a STATICALLY ANALYSABLE
 * specifier to emit a per-area chunk; a template literal with a variable in
 * it either fails the build outright or degrades to bundling every candidate
 * module into one chunk, which would silently undo the route-level code
 * splitting architecture §8 asks for and `check-precache` accounts for.
 * Verified by building it BOTH ways, not by reading rolldown's docs: the
 * emitted asset set is byte-for-byte the same names before and after this
 * task's change (`pipeline-*.js`, `jobs-*.js`, `site-ops-*.js`, `money-*.js`,
 * `costs-*.js`, `admin-*.js`, `portal-*.js`, plus `index-*` and
 * `StyleguideRoute-*`), and `check-precache` reports the identical "33
 * emitted, 21 precached, 4 excluded" for each — measured by checking out the
 * pre-task `routes.tsx`, running `pnpm web:build`, and diffing the listing
 * against this one. The brief named this trap explicitly ("do NOT lose
 * the split to make the mapping elegant"); this is the "explicit name→loader
 * record" it prescribes.
 *
 * `Record<NavPath, ...>` is doing real work, not decoration: `NavPath`
 * (`shell/nav.ts`) is the literal union of the nav table's own paths, so
 * `tsc` rejects a nav row with no module here AND a module here with no nav
 * row. `tools/structural/web-area-routes.test.ts` re-checks both directions
 * at RUNTIME, because a type is erased by esbuild and a test run is not the
 * same event as a typecheck.
 *
 * EXPORTED for that structural test, the same precedent (and the same
 * reason) as `lazyArea` above being exported for `routes.test.tsx`: nothing
 * in `src/` imports it.
 */
export const AREA_MODULES: Record<NavPath, () => Promise<ComponentType>> = {
  '/pipeline': async () => (await import('./features/pipeline')).default,
  '/jobs': async () => (await import('./features/jobs')).default,
  '/site-ops': async () => (await import('./features/site-ops')).default,
  '/money': async () => (await import('./features/money')).default,
  '/costs': async () => (await import('./features/costs')).default,
  '/admin': async () => (await import('./features/admin')).default,
  '/portal': async () => (await import('./features/portal')).default,
}

/**
 * Wraps one area in its own capability check — the client half of web-pre-
 * deploy item 6.
 *
 * NOT A SECURITY BOUNDARY, and nothing here should ever be read as one. The
 * SERVER authorizes every command and query on its own (`packages/api`'s
 * worker; `packages/api/test/*.workers.test.ts` is what proves it), and it
 * would still refuse every request this screen would have made. What this
 * closes is the gap between what the nav HIDES and what a URL can REACH: a
 * principal who bookmarked an area, or was sent a link to one, used to get
 * the whole screen and then a wall of 403s from it. Bundle-wise the area's
 * code is downloaded either way — the whole bundle is precached (design
 * §5.1) — so there is nothing secret here to withhold, only a screen not to
 * lie with.
 *
 * A COMPONENT, not a check performed inside `lazy`, because the answer is
 * only knowable at render: `useSession()` is a react-query read that is
 * in-flight for at least one render after mount. `isLoading` therefore
 * renders NOTHING rather than the refusal — flashing "you do not have
 * access" at a principal who does, for the one frame before `GET /api/me`
 * lands, is the same class of bug `AppShell`'s own `isLoading` guard exists
 * to prevent (see its doc comment for the measured version of it). In
 * practice `AppShell` has already resolved the session before an `<Outlet>`
 * child ever mounts, so this branch is belt-and-braces for a future route
 * that mounts one of these components outside that shell.
 *
 * Applied OUTSIDE the `ErrorBoundary` `lazyArea` installs (i.e. this is the
 * outermost layer of the route's component), so the gate decides before
 * either the area OR `lazyArea`'s chunk-failure `Fallback` renders — a
 * refused principal on a stale chunk gets the refusal, not "something went
 * wrong on this screen". The cost is that a throw from this thin wrapper
 * itself falls through to `AppShell`'s boundary rather than the area's own,
 * which is the same boundary already covering everything else in the shell.
 */
function gateArea(item: NavItem, Area: ComponentType): ComponentType {
  return function GatedArea() {
    const { me, isLoading } = useSession()
    if (isLoading) return null
    if (!has(me, item.capability)) return <NoAccessScreen area={item.label} />
    return <Area />
  }
}

/**
 * One child route per `NAV_ITEMS` row — the seven that used to be written out
 * by hand here.
 *
 * `item.path.slice(1)`: these are CHILDREN of the `/` route below, so they
 * carry the relative segment (`'pipeline'`), exactly as the hand-written
 * versions did, while the table stores the absolute path the nav's `<NavLink
 * to>` needs. One `/` in one place, not two spellings of the same route.
 *
 * `lazyArea` is KEPT on every one of them, deliberately and unchanged: this
 * file's own top comment traces two hops into react-router 8.3.0's source for
 * why a REJECTED `lazy` otherwise leaves an empty `<main>` forever, with no
 * fallback and no report. Generating the routes changes which code writes
 * them, not that property.
 */
const areaRoutes: RouteObject[] = NAV_ITEMS.map((item) => ({
  path: item.path.slice(1),
  lazy: async () => {
    const { Component: Area } = await lazyArea(AREA_MODULES[item.path])
    return { Component: gateArea(item, Area) }
  },
}))

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: (
      <ErrorBoundary>
        <LoginRoute />
      </ErrorBoundary>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/set-password',
    element: (
      <ErrorBoundary>
        <SetPasswordRoute />
      </ErrorBoundary>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Moved "behind" the real route table (task instruction, and `App.tsx`'s
    // own pre-existing doc comment naming this task as the one that would):
    // before this task, `App.tsx` rendered `StyleguideRoute` unconditionally
    // as the ENTIRE app, with no router at all. Now it's an ordinary path
    // like any other — reachable, but no longer the default.
    path: '/styleguide',
    lazy: () => lazyArea(async () => (await import('./styleguide/StyleguideRoute')).StyleguideRoute),
  },
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <AppShell />
      </ErrorBoundary>
    ),
    errorElement: <RouteErrorFallback />,
    children: [
      // The seven capability-gated areas, generated from `NAV_ITEMS` — see
      // `areaRoutes` above. `/no-areas` below is NOT one of them: it is the
      // shell's own fallback for a principal holding none of the landing
      // capabilities, gated by nothing (a principal with no areas must be
      // able to read the screen that says so).
      ...areaRoutes,
      {
        path: 'no-areas',
        element: (
          <ErrorBoundary>
            <NoAreasScreen />
          </ErrorBoundary>
        ),
        errorElement: <RouteErrorFallback />,
      },
    ],
  },
  { path: '*', element: <NotFoundScreen /> },
]
